import { useEffect, useState } from 'react';

/**
 * Subscribe to a CSS media query and re-render on changes. SSR/test-safe: when
 * `matchMedia` is unavailable it reports `false` and never throws.
 *
 * Used by the patients page to switch between the desktop master–detail layout
 * and the mobile drill-in. Tests drive it by stubbing `window.matchMedia`.
 */
export function useMediaQuery(query: string): boolean {
  const get = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false;

  const [matches, setMatches] = useState(get);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    // Older Safari uses addListener/removeListener.
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
}
